import React from 'react';
import { Box, Container, Typography, Chip, Button, Divider } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Navbar from '../components/landing/Navbar';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../utils/colors';

const LINKEDIN_URL = 'https://www.linkedin.com/in/lcharles';

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="h4"
    sx={{
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      color: COLORS.ink,
      fontSize: { xs: '1.5rem', md: '1.9rem' },
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="body1"
    sx={{ color: COLORS.inkMuted, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.05rem' }, mb: 2 }}
  >
    {children}
  </Typography>
);

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: COLORS.paper }}>
      <Navbar />

      {/* Hero — brand teal gradient, mirrors ResourcesPage */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 60%, ${COLORS.primaryLight} 100%)`,
          color: '#fff',
          py: { xs: 6, md: 9 },
          px: 2,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', width: 380, height: 380, borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)', top: -160, right: -120,
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
            label="Building Percoro in public"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600,
              mb: 2, '& .MuiChip-icon': { color: '#fff' },
            }}
          />
          <Typography
            variant="h3"
            sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, mb: 1.5, fontSize: { xs: '2rem', md: '2.75rem' } }}
          >
            About Percoro
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, color: 'rgba(255,255,255,0.92)', maxWidth: 640, mx: 'auto' }}>
            The name says it all.
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 5, md: 7 } }}>
        {/* The name */}
        <Body>
          Percoro comes from the Romance languages — <em>Percorso</em> in Italian, <em>Percurso</em> in Portuguese.
          Both mean route, path, journey taken.
        </Body>
        <Body>
          That's the belief at the heart of everything we build: every great trip is defined not just by the
          destination, but by the path taken. Percoro exists to make that path smarter, more social, and worth
          remembering.
        </Body>

        <Divider sx={{ my: 3, borderColor: '#E6E0D6' }} />

        {/* Why Travel */}
        <SectionHeading>Why Travel</SectionHeading>
        <Body>
          Travel is one of the most complex experiences in anyone's life. Finding flights and hotels. Coordinating
          with friends across time zones. Discovering places the guidebooks miss. Sharing moments as they happen.
          And then years later wondering — where was that photo taken? Which trip was that? What was the name of
          that restaurant?
        </Body>
        <Body>
          Travel is full of moments worth keeping. And there has always been a surprising lack of tools that help
          you keep them well — or plan the next adventure with the people who matter most.
        </Body>
        <Body>That's the gap Percoro is built to close.</Body>

        <Divider sx={{ my: 3, borderColor: '#E6E0D6' }} />

        {/* What Percoro Is */}
        <SectionHeading>What Percoro Is</SectionHeading>
        <Body>
          Percoro is an AI-powered social travel platform built to help people discover better routes, plan
          confidently, and share experiences with your global community.
        </Body>
        <Body>
          We go beyond booking. Percoro connects travelers, friends, and real-world insights into one ecosystem —
          where routes are refined, trips evolve socially, and discovery becomes effortless. Intelligent itinerary
          design meets community knowledge, giving every explorer access to smarter decisions and richer adventures.
        </Body>
        <Body>
          We believe the future of travel is collaborative, personalized, and powered by intelligence. Percoro is
          building that future.
        </Body>

        <Divider sx={{ my: 3, borderColor: '#E6E0D6' }} />

        {/* Who Built It */}
        <SectionHeading>Who Built It</SectionHeading>
        <Body>
          Percoro was founded by Lourdes Charles — a product management and technology leader with over 25 years of
          experience, including senior roles at AT&amp;T and Wipro.
        </Body>
        <Body>
          Percoro was built AI-native from day one. Not as an experiment, but as a deliberate choice to prove what
          becomes possible when AI is led with clarity, discipline, and a real problem worth solving.
        </Body>
        <Body>
          The result is a platform that reflects both the complexity of travel and the precision of intentional
          development — built by someone who has lived both.
        </Body>

        <Divider sx={{ my: 3, borderColor: '#E6E0D6' }} />

        {/* Come along — CTAs */}
        <SectionHeading>Come along</SectionHeading>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2.5 }}>
          <Typography variant="body1" sx={{ color: COLORS.inkMuted, lineHeight: 1.7, fontSize: { xs: "1rem", md: "1.05rem" } }}>
            <strong style={{ color: COLORS.ink }}>Try the demo</strong> — see how it works, end to end.
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.inkMuted, lineHeight: 1.7, fontSize: { xs: "1rem", md: "1.05rem" } }}>
            <strong style={{ color: COLORS.ink }}>Request early access</strong> — be among the first travelers in.
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.inkMuted, lineHeight: 1.7, fontSize: { xs: "1rem", md: "1.05rem" } }}>
            <strong style={{ color: COLORS.ink }}>Follow the build</strong> — the story of building Percoro, AI-native and solo, is unfolding on LinkedIn.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayCircleOutlineIcon />}
            onClick={() => navigate('/demo')}
            sx={{
              bgcolor: COLORS.primary, color: '#fff', fontWeight: 600, textTransform: 'none',
              borderRadius: 2, px: 3, py: 1.1, '&:hover': { bgcolor: COLORS.primaryDark },
            }}
          >
            Try the demo
          </Button>
          <Button
            variant="outlined"
            startIcon={<RocketLaunchOutlinedIcon />}
            onClick={() => navigate('/early-access')}
            sx={{
              color: COLORS.primary, borderColor: COLORS.primary, fontWeight: 600, textTransform: 'none',
              borderRadius: 2, px: 3, py: 1.1, '&:hover': { borderColor: COLORS.primaryDark, bgcolor: COLORS.pastelTeal },
            }}
          >
            Request early access
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkedInIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            component="a"
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: COLORS.primary, borderColor: COLORS.primary, fontWeight: 600, textTransform: 'none',
              borderRadius: 2, px: 3, py: 1.1, '&:hover': { borderColor: COLORS.primaryDark, bgcolor: COLORS.pastelTeal },
            }}
          >
            Follow the build
          </Button>
        </Box>

        {/* Light contact pointer — routing lives on the Contact page itself */}
        <Box
          sx={{
            mt: 5, p: 2.5, borderRadius: 3, bgcolor: COLORS.pastelTeal,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: COLORS.ink }}>
            Questions?{' '}
            <Box
              component="a"
              onClick={() => navigate('/contact')}
              sx={{
                color: COLORS.primary, fontWeight: 700, cursor: 'pointer',
                textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contact us
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage;